import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, Einvoice } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SaleModal from '../Sale/SaleModal';
import CreditNoteModal from '../CreditNote/CreditNoteModal';
import DebitNoteModal from '../DebitNote/DebitNoteModal';
import { useSessionValidation } from '../../../utils/sessionValidation';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import MampillyLogo from '../Logo/MampillyLogo.png';
import {
  Button, Alert, Snackbar, Typography, Box, Tooltip,
  TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import { Refresh, Search, Receipt } from '@mui/icons-material';
import './EInvoiceIndex.css';

const INVOICE_TYPES = [
  { label: 'Sale',        value: 'sale'   },
  { label: 'Credit Note', value: 'credit' },
  { label: 'Debit Note',  value: 'debit'  },
];

const CONTROLLER_MAP = {
  sale:   'EInvoiceApi',
  credit: 'EInvoiceApi',
  debit:  'EInvoiceApi',
};

// For sale type, fetch active list only (same endpoint as SaleIndex ActiveList)
const fetchSaleRecords = async (companyId, branchId, fromDate, toDate, token) => {
  const activeRes = await fetchData('SaleApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
  return (activeRes.response_data || activeRes || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));
};

const formatDateToYYYYMMDD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDefaultFromDate = () => formatDateToYYYYMMDD(new Date());

const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

const formatDate = (d) => {
  if (!d) return '-';
  const str = d.split('T')[0];
  if (str === '1900-01-01' || str === '0001-01-01') return '-';
  const [y, m, day] = str.split('-');
  return `${day}-${m}-${y}`;
};

const EInvoiceIndex = () => {
  useSessionValidation();
  const { companyId, branchId, token } = useAuth();

  const [activeType, setActiveType]     = useState('sale');
  const [fromDate, setFromDate]         = useState(getDefaultFromDate());
  const [toDate, setToDate]             = useState(getCurrentDate());
  const [records, setRecords]           = useState([]);
  const [searchTerm, setSearchTerm]     = useState('');
  const [loading, setLoading]           = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [actionLoading, setActionLoading]   = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');

  // Cancel dialog — tracks which type: 'einvoice' or 'ewaybill'
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelItem, setCancelItem]             = useState(null);
  const [cancelRemark, setCancelRemark]         = useState('');
  const [cancelType, setCancelType]             = useState('einvoice'); // 'einvoice' | 'ewaybill'

  // Response dialog
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseDialogTitle, setResponseDialogTitle] = useState('');
  const [responseDialogData, setResponseDialogData]   = useState(null);

  // Sale view modal
  const [saleModalOpen, setSaleModalOpen]   = useState(false);
  const [saleFormData, setSaleFormData]     = useState({});
  const [saleDetails, setSaleDetails]       = useState([]);
  const [saleModalLoading, setSaleModalLoading] = useState(false);

  // Credit Note view modal
  const [creditModalOpen, setCreditModalOpen]     = useState(false);
  const [creditFormData, setCreditFormData]       = useState({});
  const [creditDetails, setCreditDetails]         = useState([]);
  const [creditModalLoading, setCreditModalLoading] = useState(false);

  // Debit Note view modal
  const [debitModalOpen, setDebitModalOpen]     = useState(false);
  const [debitFormData, setDebitFormData]       = useState({});
  const [debitDetails, setDebitDetails]         = useState([]);
  const [debitModalLoading, setDebitModalLoading] = useState(false);

  const handleViewSale = async (id) => {
    if (activeType !== 'sale') return;
    setSaleModalLoading(true);
    setSaleModalOpen(true);
    try {
      const [saleRes, companyRes, branchRes] = await Promise.allSettled([
        fetchData('SaleApi', `/${id}`, token),
        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token),
        fetchData('BranchApi', `/BranchDetails/${branchId}`, token),
      ]);
      const freshData = saleRes.value?.response_data || saleRes.value;
      const saleData  = freshData?.Sale || freshData;
      const details   = freshData?.SaleDetails || [];
      setSaleFormData({
        ...saleData,
        BillDate:     saleData.BillDate?.split('T')[0]     || '',
        ClearingDate: saleData.ClearingDate?.split('T')[0] || '',
      });
      setSaleDetails(details);
      if (companyRes.status === 'fulfilled') setPdfCompanyData(companyRes.value?.response_data || companyRes.value);
      if (branchRes.status  === 'fulfilled') setPdfBranchData(branchRes.value?.response_data  || branchRes.value);
    } catch (err) {
      console.error('Error fetching sale details:', err);
    } finally {
      setSaleModalLoading(false);
    }
  };

  const [pdfCompanyData, setPdfCompanyData] = useState(null);
  const [pdfBranchData,  setPdfBranchData]  = useState(null);

  const handleViewCreditNote = async (id) => {
    setCreditModalLoading(true);
    setCreditModalOpen(true);
    try {
      const res = await fetchData('CreditNoteApi', `/${id}`, token);
      const freshData = res?.response_data || res;
      const noteData  = freshData?.CreditNote || freshData;
      const details   = freshData?.CreditNoteDetails || [];
      setCreditFormData({
        ...noteData,
        BillDate: noteData.BillDate?.split('T')[0] || '',
      });
      setCreditDetails(details);
    } catch (err) {
      console.error('Error fetching credit note details:', err);
      setError('Failed to fetch credit note details.');
    } finally {
      setCreditModalLoading(false);
    }
  };

  const handleViewDebitNote = async (id) => {
    setDebitModalLoading(true);
    setDebitModalOpen(true);
    try {
      const res = await fetchData('DebitNoteApi', `/${id}`, token);
      const freshData = res?.response_data || res;
      const noteData  = freshData?.DebitNote || freshData;
      const details   = freshData?.DebitNoteDetails || [];
      setDebitFormData({
        ...noteData,
        BillDate: noteData.BillDate?.split('T')[0] || '',
      });
      setDebitDetails(details);
    } catch (err) {
      console.error('Error fetching debit note details:', err);
      setError('Failed to fetch debit note details.');
    } finally {
      setDebitModalLoading(false);
    }
  };

  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    const ones  = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine'];
    const teens = ['Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
    const tens  = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
    const cvt = (n) => {
      let r = '';
      if (n >= 100) { r += ones[Math.floor(n/100)] + ' Hundred '; n %= 100; }
      if (n >= 20)  { r += tens[Math.floor(n/10)] + ' '; n %= 10; }
      else if (n >= 10) { r += teens[n-10] + ' '; return r; }
      if (n > 0) r += ones[n] + ' ';
      return r;
    };
    let result = '', n = num;
    const cr = Math.floor(n/10000000); if (cr > 0) { result += cvt(cr) + 'Crore '; n %= 10000000; }
    const lk = Math.floor(n/100000);   if (lk > 0) { result += cvt(lk) + 'Lakh ';  n %= 100000;  }
    const th = Math.floor(n/1000);     if (th > 0) { result += cvt(th) + 'Thousand '; n %= 1000;  }
    if (n > 0) result += cvt(n);
    return result.trim();
  };

  const generateBillPDF = async () => {
    const saleData = saleFormData;
    const details  = saleDetails;
    if (!saleData?.Id) return null;

    const blue=[30,100,200], red=[220,30,30], white=[255,255,255];
    const lightBlue=[235,244,255], black=[0,0,0], gray=[100,100,100], lineGray=[200,210,220];

    const coName  = pdfCompanyData?.Name || 'Company Name';
    const coGST   = pdfBranchData?.GSTNumber || pdfCompanyData?.GSTNumber || '';
    const coAddr  = [pdfBranchData?.AddressLine1, pdfBranchData?.AddressLine2, pdfBranchData?.Location, pdfBranchData?.PinCode].filter(Boolean).join(', ') || pdfCompanyData?.Address || '';
    const coTel   = pdfBranchData?.ContactNumber1 || pdfCompanyData?.ContactNumber || '';
    const coEmail = pdfBranchData?.Email || pdfCompanyData?.Email || '';

    const custName  = saleData.CustomerName   || '-';
    const custGST   = saleData.GSTNumber      || '';
    const custAddr1 = saleData.ShipToAddress1 || '';
    const custAddr2 = saleData.ShipToAddress2 || '';
    const custLoc   = saleData.ShipToLocation || '';
    const custPin   = saleData.ShipToPincode  || '';
    const billNo    = `${saleData.BillPrefix ? saleData.BillPrefix+'/' : ''}${saleData.BillNumber||''}${saleData.FinYear ? '/'+saleData.FinYear : ''}`;
    const billDate  = formatDate(saleData.BillDate);
    const salesManName = saleData.SalesManName || 'N/A';

    const pdf = new jsPDF('p','mm','a4');
    const PW = pdf.internal.pageSize.width, PH = pdf.internal.pageSize.height, M = 5, CW = PW - 2*M;

    const T = (text, x, y, opts={}) => {
      const { fs=9, fw='normal', col=black, align='left', mw=null } = opts;
      pdf.setFontSize(fs); pdf.setFont('helvetica', fw); pdf.setTextColor(...col);
      const str = String(text ?? '');
      if (mw) { const lines = pdf.splitTextToSize(str, mw); pdf.text(lines, x, y, { align }); return y + lines.length * fs * 0.38; }
      pdf.text(str, x, y, { align }); return y + fs * 0.38;
    };
    const R = (x, y, w, h, opts={}) => {
      const { fill=null, stroke=null, lw=0.3, r=0 } = opts;
      if (fill)   pdf.setFillColor(...fill);
      if (stroke) { pdf.setDrawColor(...stroke); pdf.setLineWidth(lw); } else { pdf.setDrawColor(...lineGray); pdf.setLineWidth(lw); }
      if (r > 0) pdf.roundedRect(x, y, w, h, r, r, fill && stroke ? 'FD' : fill ? 'F' : 'S');
      else pdf.rect(x, y, w, h, fill && stroke ? 'FD' : fill ? 'F' : 'S');
    };
    const line = (x1, y1, x2, y2, col=lineGray, lw=0.3) => { pdf.setDrawColor(...col); pdf.setLineWidth(lw); pdf.line(x1,y1,x2,y2); };

    let y = M;
    const headerH = 24, logoSize = 18;
    pdf.addImage(MampillyLogo, 'PNG', M, y + (headerH-logoSize)/2, logoSize, logoSize);
    const infoX = M + logoSize + 4;
    T(coName, infoX, y+7, { fs:13, fw:'bold', col:blue });
    let iy = y+12;
    if (coAddr)  iy = T(coAddr, infoX, iy, { fs:7.5, col:gray, mw:120 });
    if (coGST)   iy = T(`GST: ${coGST}`, infoX, iy+1, { fs:7.5, col:gray });
    const telEmail = [coTel && `Tel: ${coTel}`, coEmail && `Email: ${coEmail}`].filter(Boolean).join(' | ');
    if (telEmail) T(telEmail, infoX, iy+1, { fs:7.5, col:gray });
    T('TAX INVOICE', PW-M, y+8, { fs:13, fw:'bold', col:red, align:'right' });
    y += headerH + 2;
    line(M, y, PW-M, y, blue, 0.4);
    y += 5;

    const billBoxH = 42;
    R(M, y, CW, billBoxH, { fill:lightBlue, stroke:blue, lw:0.6, r:3 });
    const leftW = CW * 0.55;
    let ly = y+6;
    T('BILL TO:', M+5, ly, { fs:8, fw:'bold', col:black }); ly += 5;
    T(custName, M+5, ly, { fs:10, fw:'bold', col:blue }); ly += 5;
    if (custAddr1) ly = T(custAddr1, M+5, ly, { fs:8, col:black, mw:leftW-8 });
    if (custAddr2) ly = T(custAddr2, M+5, ly+1, { fs:8, col:black, mw:leftW-8 });
    if (custLoc||custPin) T([custLoc,custPin].filter(Boolean).join(' - '), M+5, ly+1, { fs:8, col:black });
    if (custGST) T(`GST No: ${custGST}`, M+5, ly+5, { fs:8, col:black });
    const rx = M+leftW+4, valX = PW-M-5;
    let ry = y+7;
    const infoRow = (label, value) => { T(label, rx, ry, { fs:8, fw:'bold', col:black }); T(value, valX, ry, { fs:8, col:blue, align:'right' }); ry += 6; };
    infoRow('Invoice No:', billNo); infoRow('Invoice Date:', billDate); infoRow('Salesman:', salesManName);
    if (saleData.VehicleNumber)   infoRow('Vehicle No:',  saleData.VehicleNumber);
    if (saleData.TransporterName) infoRow('Transporter:', saleData.TransporterName);
    y += billBoxH + 6;

    const hasIGST   = details.some(d => (d.IGSTAmount||0) > 0) || (saleData.IGSTAmount||0) > 0;
    const hasCSSGST = details.some(d => (d.CGSTAmount||0) > 0 || (d.SGSTAmount||0) > 0);
    let tableHead, tableBody;
    if (hasIGST && !hasCSSGST) {
      tableHead = [['#','Description','HSN','Qty','Rate','GST%','Taxable','IGST Amt','Total']];
      tableBody = details.map((d,i) => [i+1, d.ItemName||`Item ${d.ItemId}`, d.HSNCode||'', Number(d.Quantity||0).toFixed(2), Number(d.Rate||0).toFixed(2), d.GSTRate?`${d.GSTRate}%`:'0%', Number(d.TaxableAmount||0).toFixed(2), Number(d.IGSTAmount||0).toFixed(2), Number(d.TotalAmount||0).toFixed(2)]);
    } else {
      tableHead = [['#','Description','HSN','Qty','Rate','GST%','Taxable','CGST','SGST','Total']];
      tableBody = details.map((d,i) => [i+1, d.ItemName||`Item ${d.ItemId}`, d.HSNCode||'', Number(d.Quantity||0).toFixed(2), Number(d.Rate||0).toFixed(2), d.GSTRate?`${d.GSTRate}%`:'0%', Number(d.TaxableAmount||0).toFixed(2), Number(d.CGSTAmount||0).toFixed(2), Number(d.SGSTAmount||0).toFixed(2), Number(d.TotalAmount||0).toFixed(2)]);
    }
    autoTable(pdf, {
      startY: y, head: tableHead, body: tableBody, theme: 'grid',
      headStyles: { fillColor: blue, textColor: white, fontSize: 8, fontStyle: 'bold', halign: 'center', cellPadding: 2.5 },
      bodyStyles: { fontSize: 8, textColor: black, cellPadding: 2 },
      columnStyles: hasIGST && !hasCSSGST
        ? { 0:{halign:'center',cellWidth:8}, 1:{halign:'left',cellWidth:57}, 2:{halign:'center',cellWidth:20}, 3:{halign:'right',cellWidth:14}, 4:{halign:'right',cellWidth:18}, 5:{halign:'center',cellWidth:14}, 6:{halign:'right',cellWidth:22}, 7:{halign:'right',cellWidth:20}, 8:{halign:'right',cellWidth:24} }
        : { 0:{halign:'center',cellWidth:8}, 1:{halign:'left',cellWidth:53}, 2:{halign:'center',cellWidth:21}, 3:{halign:'right',cellWidth:13}, 4:{halign:'right',cellWidth:16}, 5:{halign:'center',cellWidth:13}, 6:{halign:'right',cellWidth:20}, 7:{halign:'right',cellWidth:18}, 8:{halign:'right',cellWidth:18}, 9:{halign:'right',cellWidth:20} },
      margin: { left:M, right:M }, alternateRowStyles: { fillColor:[245,249,255] }, tableLineColor: blue, tableLineWidth: 0.3,
    });
    y = (pdf.lastAutoTable?.finalY || y+30) + 6;

    const summaryW = 80, summaryX = PW-M-summaryW, leftColW = summaryX-M-4;
    const summaryRows = [
      ['Subtotal:',    Number(saleData.Amount||0).toFixed(2)],
      ['CGST Amount:', Number(saleData.CGSTAmount||0).toFixed(2)],
      ['SGST Amount:', Number(saleData.SGSTAmount||0).toFixed(2)],
      ['IGST Amount:', Number(saleData.IGSTAmount||0).toFixed(2)],
      ['Discount:',    Number(saleData.DiscountAmount||0).toFixed(2)],
      ['Freight:',     Number(saleData.FreightAmount||0).toFixed(2)],
      ['Freight GST:', Number(saleData.FreightGSTAmount||0).toFixed(2)],
      ['Packing:',     Number(saleData.PackingAmount||0).toFixed(2)],
      ['Packing GST:', Number(saleData.PackingGSTAmount||0).toFixed(2)],
      ['TCS:',         Number(saleData.TCSAmount||0).toFixed(2)],
      ['Round Off:',   Number(saleData.RoundOff||0).toFixed(2)],
    ].filter(([,v]) => parseFloat(v) !== 0);
    const summaryRowH = 6, grandTotalH = 10;
    const payDetailRows = [
      saleData.CashAmount    > 0 && ['Cash:',        Number(saleData.CashAmount).toFixed(2)],
      saleData.UPIAmount     > 0 && ['UPI:',         Number(saleData.UPIAmount).toFixed(2)],
      saleData.CardAmount    > 0 && ['Card:',        Number(saleData.CardAmount).toFixed(2)],
      saleData.BalanceAmount > 0 && ['Balance Due:', Number(saleData.BalanceAmount).toFixed(2)],
    ].filter(Boolean);
    const payDetailH = payDetailRows.length > 0 ? payDetailRows.length * summaryRowH + 8 : 0;
    const totalBoxH = summaryRows.length * summaryRowH + 2 + grandTotalH + payDetailH + 4;
    R(summaryX, y, summaryW, totalBoxH, { fill:white, stroke:blue, lw:0.6, r:3 });
    let sy = y+4;
    summaryRows.forEach(([label, val]) => { T(label, summaryX+4, sy, { fs:8, col:black }); T(val, summaryX+summaryW-4, sy, { fs:8, col:black, align:'right' }); sy += summaryRowH; });
    line(summaryX+2, sy, summaryX+summaryW-2, sy, blue, 0.5); sy += 2;
    R(summaryX, sy, summaryW, grandTotalH, { fill:blue, stroke:blue, lw:0.5 });
    T('GRAND TOTAL:', summaryX+4, sy+7, { fs:10, fw:'bold', col:white });
    T(Number(saleData.TotalAmount||saleData.GrandTotal||0).toFixed(2), summaryX+summaryW-4, sy+7, { fs:10, fw:'bold', col:white, align:'right' });
    sy += grandTotalH + 2;
    if (payDetailRows.length > 0) {
      T('Payment Details:', summaryX+4, sy+4, { fs:8, fw:'bold', col:black }); sy += 7;
      payDetailRows.forEach(([label, val]) => { T(label, summaryX+4, sy, { fs:8, col:black }); T(val, summaryX+summaryW-4, sy, { fs:8, col:black, align:'right' }); sy += summaryRowH; });
    }
    let lly = y;
    if (saleData.Remarks) { T('Remarks:', M, lly, { fs:8, fw:'bold', col:black }); lly += 5; lly = T(saleData.Remarks, M, lly, { fs:8, col:gray, mw:leftColW }); lly += 4; }
    const grandTotal = saleData.TotalAmount || saleData.GrandTotal || 0;
    T('Amount in Words:', M, lly, { fs:8, fw:'bold', col:black }); lly += 5;
    T(`Rupees ${numberToWords(Math.floor(grandTotal))} Only`, M, lly, { fs:8, col:gray, mw:leftColW });

    const eInvAckNo = saleData.AckNo || '', eInvAckDt = saleData.AckDt || '', eInvIrn = saleData.Irn || '', signedQrCode = saleData.SignedQrCode || '';
    if (signedQrCode || eInvAckNo || eInvIrn) {
      const eInvY = PH-56, qrSize = 35, eInvBoxH = 38;
      R(M, eInvY, CW, eInvBoxH, { fill:[245,249,255], stroke:blue, lw:0.4, r:2 });
      T('E-INVOICE DETAILS', M+4, eInvY+6, { fs:8, fw:'bold', col:blue });
      if (signedQrCode) {
        try {
          const qrDataUrl = await QRCode.toDataURL(signedQrCode, { width:150, margin:1, errorCorrectionLevel:'M' });
          pdf.addImage(qrDataUrl, 'PNG', PW-M-qrSize-2, eInvY+2, qrSize, qrSize-4);
        } catch (e) { console.error('QR error:', e); }
      }
      let ey = eInvY+12;
      const eRow = (label, value) => { if (!value) return; T(label, M+4, ey, { fs:7.5, fw:'bold', col:black }); T(value, M+32, ey, { fs:7.5, col:black, mw:CW-qrSize-36 }); ey += 6; };
      eRow('Ack No :', eInvAckNo); eRow('Ack Date:', eInvAckDt ? formatDate(eInvAckDt) : ''); eRow('IRN     :', eInvIrn);
    }

    const footerY = PH-22;
    T('Authorized Signature', M, footerY+8, { fs:9, fw:'bold', col:black });
    T('Customer Signature', PW-M, footerY+8, { fs:9, fw:'bold', col:black, align:'right' });
    T('This is a computer-generated invoice and does not require a signature.', PW/2, footerY+16, { fs:7, col:gray, align:'center' });

    return { pdf, saleData };
  };

  const handlePrintBill = async () => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');
    try {
      const result = await generateBillPDF();
      if (!result) { setError('Failed to generate PDF.'); setLoading(false); return; }
      const { pdf, saleData } = result;
      pdf.autoPrint();
      const blob = pdf.output('blob');
      const url  = URL.createObjectURL(blob);
      const win  = window.open(url, '_blank');
      if (win) {
        win.onload = () => { win.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = `Invoice_${saleData.BillNumber}.pdf`; a.click();
        URL.revokeObjectURL(url);
      }
      setSuccess(`Invoice ${saleData.BillNumber} ready for printing.`);
    } catch (err) {
      console.error(err);
      setError('Failed to generate PDF.');
    }
    setLoading(false);
  };

  const fetchRecords = useCallback(async () => {
    if (!companyId || !branchId || !token) return;
    setLoading(true);
    setLoadingMessage('Loading E-Invoice records...');
    setError('');
    try {
      let data = [];
      if (activeType === 'sale') {
        // Same fetch pattern as SaleIndex — active + inactive combined
        data = await fetchSaleRecords(companyId, branchId, fromDate, toDate, token);
      } else {
        const controller = CONTROLLER_MAP[activeType];
        const endpoint   = `/EInvoiceList/${companyId}/${branchId}/${fromDate}/${toDate}`;
        const response   = await fetchData(controller, endpoint, token);
        data = response.response_data || response || [];
      }
      setRecords(data);
    } catch (err) {
      console.error('Error fetching E-Invoice records:', err);
      setError('Failed to fetch E-Invoice records.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, token, activeType, fromDate, toDate]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Create / Generate E-Invoice
  const handleCreateEInvoice = async (id) => {
    setLoading(true);
    setLoadingMessage('Creating E-Invoice...');
    setError('');
    try {
      const res = await Einvoice('EInvoiceApi', id, 'Create', token);
      const isSuccess = res?.status === true || res?.status === 'true' || res?.success === true || res?.AckNo || res?.irn || res?.Irn;
      if (isSuccess) {
        setSuccess('E-Invoice created successfully!');
        await fetchRecords();
      } else {
        setResponseDialogTitle('E-Invoice Response');
        setResponseDialogData(res);
        setResponseDialogOpen(true);
      }
    } catch (err) {
      console.error('Error creating E-Invoice:', err);
      setError(err?.response?.data?.message || 'Failed to create E-Invoice.');
    } finally {
      setLoading(false);
    }
  };

  // Regenerate E-Invoice
  const handleRegenerateEInvoice = async (id) => {
    setLoading(true);
    setLoadingMessage('Regenerating E-Invoice...');
    setError('');
    try {
      const res = await Einvoice('EInvoiceApi', id, 'Regenerate', token);
      const isSuccess = res?.status === true || res?.status === 'true' || res?.success === true || res?.AckNo || res?.irn || res?.Irn;
      if (isSuccess) {
        setSuccess('E-Invoice regenerated successfully!');
        await fetchRecords();
      } else {
        setResponseDialogTitle('E-Invoice Response');
        setResponseDialogData(res);
        setResponseDialogOpen(true);
      }
    } catch (err) {
      console.error('Error regenerating E-Invoice:', err);
      setError(err?.response?.data?.message || 'Failed to regenerate E-Invoice.');
    } finally {
      setLoading(false);
    }
  };

  // Create Eway Bill
  const handleCreateEwayBill = async (id) => {
    setLoading(true);
    setLoadingMessage('Creating Eway Bill...');
    setError('');
    try {
      const res = await Einvoice('EInvoiceApi', id, 'EwayBill', token);
      const isSuccess = res?.status === true || res?.status === 'true' || res?.success === true || res?.EwbNo || res?.ewbNo;
      if (isSuccess) {
        setSuccess('Eway Bill created successfully!');
        await fetchRecords();
      } else {
        setResponseDialogTitle('Eway Bill Response');
        setResponseDialogData(res);
        setResponseDialogOpen(true);
      }
    } catch (err) {
      console.error('Error creating Eway Bill:', err);
      setError(err?.response?.data?.message || 'Failed to create Eway Bill.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCancel = (item, type) => {
    setCancelItem(item);
    setCancelType(type);
    setCancelRemark('');
    setCancelDialogOpen(true);
  };

  const handleCloseCancel = () => {
    setCancelDialogOpen(false);
    setCancelItem(null);
    setCancelRemark('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelItem) return;
    setActionLoading(true);
    setError('');
    try {
      const endpoint = cancelType === 'ewaybill'
        ? `CancelEwayBill/${encodeURIComponent(cancelRemark || 'Cancelled')}`
        : `Cancel`;
      await Einvoice('EInvoiceApi', cancelItem.Id, endpoint, token);
      setSuccess(cancelType === 'ewaybill' ? 'Eway Bill cancelled successfully!' : 'E-Invoice cancelled successfully!');
      handleCloseCancel();
      await fetchRecords();
    } catch (err) {
      console.error('Error cancelling:', err);
      setError(err?.response?.data?.message || 'Failed to cancel.');
    } finally {
      setActionLoading(false);
    }
  };

  const isSent      = (row) => !!(row.AckNo);
  const isCancelled = (row) => !!(row.EInvoiceCancelled || row.IsCancelled);

  const getStatusChip = (row) => {
    if (isCancelled(row)) return <span className="einv-status-chip cancelled">Cancelled</span>;
    if (isSent(row))      return <span className="einv-status-chip sent">Sent</span>;
    return <span className="einv-status-chip pending">Pending</span>;
  };

  // Filter by search
  const filteredRecords = records.filter((r) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      String(r.BillNumber || '').toLowerCase().includes(term) ||
      String(r.CustomerName || '').toLowerCase().includes(term) ||
      String(r.GSTNumber || '').toLowerCase().includes(term) ||
      String(r.IRN || '').toLowerCase().includes(term)
    );
  });

  const handleCloseSnackbar = () => { setSuccess(''); setError(''); };

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading || actionLoading} message={loadingMessage} />

      <div style={{
        maxWidth: '1400px', margin: '0 auto', background: 'white',
        borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '12px', position: 'relative', color: 'black',
        fontFamily: 'Times New Roman'
      }}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Receipt sx={{ color: '#2C3E6E', fontSize: 22 }} />
            <h4 style={{
              fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700',
              margin: 0, textTransform: 'uppercase', color: 'black'
            }}>
              E-Invoice Management
            </h4>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search by Bill No, Customer, GST..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#667eea', fontSize: '20px' }} />
                </InputAdornment>
              )
            }}
            sx={{
              flex: 1, minWidth: 180,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', backgroundColor: '#f8f9fa',
                fontSize: '13px', height: '29px'
              }
            }}
          />

          {/* Date range */}
          <input
            type="date" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}
          />
          <input
            type="date" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}
          />

          {/* Type toggle buttons */}
          {INVOICE_TYPES.map((t) => (
            <button
              key={t.value}
              className={`einv-type-btn${activeType === t.value ? ' active' : ''}`}
              onClick={() => setActiveType(t.value)}
            >
              {t.label}
            </button>
          ))}

          {/* Refresh */}
          <Tooltip title="Refresh" arrow>
            <IconButton
              onClick={fetchRecords}
              disabled={loading}
              size="small"
              sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}
            >
              <Refresh sx={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* ── Table ── */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            borderRadius: '10px',
            overflow: 'hidden',
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'scroll',
            overflowX: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: '#2C3E6E #f0f0f0',
          }}>
            <Table sx={{ minWidth: 900 }} stickyHeader>
              <TableHead>
                <TableRow style={{ height: '27px' }}>
                  {['Sl No.', 'Bill No.', 'Date', 'Customer', 'GST No', 'Amount', 'Ack No', 'Ack Date', 'EWB No', 'Status', 'Actions'].map((h, i) => (
                    <TableCell
                      key={i}
                      style={{
                        background: '#2C3E6E',
                        color: 'white', fontWeight: '600', fontSize: '11px',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        padding: '4px 12px', height: '27px',
                        textAlign: i === 5 ? 'right' : i === 10 ? 'center' : 'left',
                        whiteSpace: 'nowrap',
                        borderBottom: 'none',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map((row, index) => (
                    <TableRow
                      key={row.Id || index}
                      style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}
                    >
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        <div
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            cursor: activeType !== 'sale' && activeType !== 'credit' && activeType !== 'debit' ? 'default' : 'pointer',
                            color: '#2C3E6E',
                            textDecoration: 'underline'
                          }}
                          onClick={() => {
                            if (activeType === 'sale') handleViewSale(row.Id);
                            else if (activeType === 'credit') handleViewCreditNote(row.Id);
                            else if (activeType === 'debit') handleViewDebitNote(row.Id);
                          }}
                        >
                          <Receipt sx={{ fontSize: 15, color: '#2C3E6E' }} />
                          {row.BillPrefix ? `${row.BillPrefix}/` : ''}{row.BillNumber || '-'}{row.FinYear ? `/${row.FinYear}` : ''}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {formatDate(row.BillDate)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {row.CustomerName || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {row.GSTNumber || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>
                        {row.GrandTotal != null ? `₹${parseFloat(row.GrandTotal).toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {row.AckNo || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {formatDate(row.AckDt)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {row.EwbNo || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        {getStatusChip(row)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>

                          {row.GSTNumber ? (
                            /* ── Has GST → E-Invoice actions only ── */
                            <>
                              {!isSent(row) && !isCancelled(row) && (
                                <Tooltip title="Create E-Invoice" arrow>
                                  <span
                                    style={{ cursor: loading ? 'not-allowed' : 'pointer', fontSize: '18px', opacity: loading ? 0.4 : 1 }}
                                    onClick={() => !loading && handleCreateEInvoice(row.Id)}
                                  >🧾</span>
                                </Tooltip>
                              )}
                              {isSent(row) && !isCancelled(row) && (
                                <>
                                  <Tooltip title="Cancel E-Invoice" arrow>
                                    <span
                                      style={{ cursor: loading ? 'not-allowed' : 'pointer', fontSize: '18px', opacity: loading ? 0.4 : 1 }}
                                      onClick={() => !loading && handleOpenCancel(row, 'einvoice')}
                                    >❌</span>
                                  </Tooltip>
                                  <Tooltip title="Regenerate E-Invoice" arrow>
                                    <span
                                      style={{ cursor: loading ? 'not-allowed' : 'pointer', fontSize: '18px', opacity: loading ? 0.4 : 1 }}
                                      onClick={() => !loading && handleRegenerateEInvoice(row.Id)}
                                    >🔄</span>
                                  </Tooltip>
                                </>
                              )}
                              {isCancelled(row) && (
                                <span style={{ fontSize: '12px', color: '#991b1b', fontWeight: 600 }}>Cancelled</span>
                              )}
                            </>
                          ) : (
                            /* ── No GST → Eway Bill actions only ── */
                            <>
                              {!row.EwbNo && !isCancelled(row) && (
                                <Tooltip title="Create Eway Bill" arrow>
                                  <span
                                    style={{ cursor: loading ? 'not-allowed' : 'pointer', fontSize: '18px', opacity: loading ? 0.4 : 1 }}
                                    onClick={() => !loading && handleCreateEwayBill(row.Id)}
                                  >🚚</span>
                                </Tooltip>
                              )}
                              {row.EwbNo && !isCancelled(row) && (
                                <Tooltip title="Cancel Eway Bill" arrow>
                                  <span
                                    style={{ cursor: loading ? 'not-allowed' : 'pointer', fontSize: '18px', opacity: loading ? 0.4 : 1 }}
                                    onClick={() => !loading && handleOpenCancel(row, 'ewaybill')}
                                  >🚫</span>
                                </Tooltip>
                              )}
                              {isCancelled(row) && (
                                <span style={{ fontSize: '12px', color: '#991b1b', fontWeight: 600 }}>Cancelled</span>
                              )}
                            </>
                          )}

                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                      No E-Invoice records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* ── Sale View Modal ── */}
      <SaleModal
        open={saleModalOpen}
        onClose={() => { setSaleModalOpen(false); setSaleFormData({}); setSaleDetails([]); }}
        modalAction="view"
        formData={saleFormData}
        setFormData={setSaleFormData}
        saleDetails={saleDetails}
        setSaleDetails={setSaleDetails}
        onSave={() => {}}
        customers={[]}
        items={[]}
        banks={[]}
        loading={saleModalLoading}
        freightPackingGST={{ freight: null, packing: null }}
        companyId={companyId}
        branchId={branchId}
        token={token}
        onPrint={handlePrintBill}
      />

      {/* ── Credit Note View Modal ── */}
      <CreditNoteModal
        open={creditModalOpen}
        onClose={() => { setCreditModalOpen(false); setCreditFormData({}); setCreditDetails([]); }}
        modalAction="view"
        formData={creditFormData}
        setFormData={setCreditFormData}
        noteDetails={creditDetails}
        setNoteDetails={setCreditDetails}
        onSave={() => fetchRecords()}
        loading={creditModalLoading}
        companyId={companyId}
        branchId={branchId}
        token={token}
      />

      {/* ── Debit Note View Modal ── */}
      <DebitNoteModal
        open={debitModalOpen}
        onClose={() => { setDebitModalOpen(false); setDebitFormData({}); setDebitDetails([]); }}
        modalAction="view"
        formData={debitFormData}
        setFormData={setDebitFormData}
        noteDetails={debitDetails}
        setNoteDetails={setDebitDetails}
        onSave={() => fetchRecords()}
        loading={debitModalLoading}
        companyId={companyId}
        branchId={branchId}
        token={token}
      />

      {/* ── API Response Dialog ── */}
      <Dialog open={responseDialogOpen} onClose={() => setResponseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '15px', color: '#2C3E6E', borderBottom: '1px solid #f0f0f0', pb: 1 }}>
          🧾 {responseDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {responseDialogData && (
            <Typography sx={{ fontSize: '13px', color: '#dc2626', fontFamily: 'Times New Roman' }}>
              {responseDialogData.response || responseDialogData.Response || 'No response message.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)} variant="contained" sx={{ textTransform: 'none', background: '#2C3E6E' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Confirmation Dialog ── */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#dc2626', fontWeight: 700, fontSize: '15px' }}>
          {cancelType === 'ewaybill' ? '🚫 Cancel Eway Bill' : '❌ Cancel E-Invoice'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: '13px' }}>
            Are you sure you want to cancel the{' '}
            <strong>{cancelType === 'ewaybill' ? 'Eway Bill' : 'E-Invoice'}</strong> for bill{' '}
            <strong>
              {cancelItem?.BillPrefix ? `${cancelItem.BillPrefix}/` : ''}{cancelItem?.BillNumber}
            </strong>?
          </Typography>
          <TextField
            label="Cancellation Remark"
            fullWidth
            size="small"
            value={cancelRemark}
            onChange={(e) => setCancelRemark(e.target.value)}
            placeholder="Enter reason for cancellation"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancel} disabled={actionLoading} sx={{ textTransform: 'none' }}>
            No
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={actionLoading}
            sx={{ textTransform: 'none' }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbars ── */}
      <Snackbar open={!!success} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default EInvoiceIndex;

